import React, { useState, useEffect } from 'react';
import { propOr, path, isEmpty, compose, prop, find, values } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import {
  AppProvider,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Form,
  FormLayout,
  Frame,
  Badge,
  TextStyle,
  TextContainer,
  Heading,
  InlineError,
  Layout,
  Page,
  Modal,
  Stack,
  Select,
  RangeSlider,
  Thumbnail,
  Caption,
  DropZone,
  Link,
  TextField,
  Toast,
  Subheading,
} from '@shopify/polaris';
import SuccessBanner from '../components/SuccessBanner';
import { LockMinor } from '@shopify/polaris-icons';

import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { CREATE_SHOP_CAREER_PORTAL, UPDATE_SHOP_CAREER_PORTAL } from '../apollo/mutation';

import ColorPicker from '../components/ColorPicker';

import { defaultWhiteColor, defaultGrayColor, defaultDarkGrayColor, hslaColor } from '../../utils/defaultColors';
import {
  HEADING_TEXT,
  SUBHEADING_TEXT,
  PAGE_TITLE,
  META_DESCRIPTION,
  FONT_WEIGHT_OPTIONS,
  FONT_FAMILY_OPTIONS,
  FONT_CASING_OPTIONS,
  CONTENT_ALIGNMENT_OPTIONS,
} from '../../utils/formDefaultValues';
import { onSubmitFailed } from '../../utils/errors';
import { keysToCamel } from '../../utils/normalizer';
import { uploadImageUrl } from '../../utils/imagesUrl';

const HeroSection = ({ history, shopifyToken, shopifyDomain, careerPageLink }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const shop = propOr({}, 'shop')(subscriptionData);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;

  const [form, setForm] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isSaveBarActive, setIsSaveBarActive] = useState(false);
  const [heroBackgroundChanged, setHeroBackgroundChanged] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const [opacityValue, setOpacityValue] = useState(100);
  const [careerPortalTime, setCareerPortalTime] = useState();
  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const handleOpacityChange = (value) => {
    updateOpacity(value);
    setOpacityValue(value);
  };

  const togglePreviewModal = () => setPreview(!preview);
  const [formErrors, setFormErrors] = useState({
    headingText: false,
    subheadingText: false,
    buttonText: false,
  });
  const [file, setFile] = useState();
  const handleDropZoneDrop = (_dropFiles, acceptedFiles, _rejectedFiles) => {
    updateImage(acceptedFiles[0]);
    setFile(acceptedFiles[0]);
  };

  const handleRemoveBackgroundImage = () => {
    updateImage(null);
    setFile(null);
    setHeroBackgroundChanged(true);
  };

  const validImageTypes = ['image/gif', 'image/jpeg', 'image/png'];
  const fileUpload = !file && <DropZone.FileUpload actionTitle='Add image' actionHint='or drop an file to upload' />;

  const uploadedFile = file && (
    <Stack>
      <Thumbnail
        size='small'
        alt={file.name}
        source={
          validImageTypes.indexOf(file.type) > 0
            ? window.URL.createObjectURL(file)
            : 'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
        }
      />
      <div>
        {file.name} <Caption>{file.size} bytes</Caption>
      </div>
    </Stack>
  );

  const renderPreviewPhoto = (placeholder = true) => {
    if (!!file) return window.URL.createObjectURL(file);
    if (form?.heroBackgroundImage?.name) {
      return window.URL.createObjectURL(form?.heroBackgroundImage);
    }
    if (form?.heroBackgroundImage) return form?.heroBackgroundImage;
    return placeholder ? uploadImageUrl : '';
  };

  const filterPageTimeLabel = (label) => {
    if (!careerPortalTime) return;
    return careerPortalTime.filter((val) => val.attribute == label)[0].label;
  };

  useEffect(() => {
    const careerPortal = propOr({}, 'careerPortal')(data);
    const headingText = propOr(HEADING_TEXT, 'headingText')(careerPortal);
    const heroBackgroundImage = propOr('', 'heroBackgroundImageReadPath')(careerPortal);
    const subheadingText = propOr(SUBHEADING_TEXT, 'subheadingText')(careerPortal);
    const pageTitle = propOr(PAGE_TITLE, 'pageTitle')(careerPortal);
    const metaDescription = propOr(META_DESCRIPTION, 'metaDescription')(careerPortal);
    const openRolesButton = propOr(false, 'openRolesButton')(careerPortal);
    const buttonText = propOr('', 'buttonText')(careerPortal);
    const styleControls = propOr({}, 'styleControls')(careerPortal);
    const heroSection = propOr({}, 'hero_section')(styleControls);
    const styleControlsFormatted = keysToCamel(careerPortal.styleControls);
    const talentCommunitySection = propOr(false, 'talentCommunitySection')(careerPortal);
    const sectionHero = propOr(false, 'sectionHero')(careerPortal);
    setCareerPortalTime(careerPortal.pageTimestamps);
    setOpacityValue(Number(heroSection?.background_opacity || '1') * 100);
    setForm((prevForm) => ({
      ...prevForm,
      headingText,
      subheadingText,
      pageTitle,
      metaDescription,
      openRolesButton,
      buttonText,
      heroBackgroundImage,
      sectionHero,
      ...styleControlsFormatted,
      heroSection: {
        ...prevForm.heroSection,
        backgroundColor: heroSection?.background_color || defaultWhiteColor,
        backgroundOpacity: heroSection?.background_opacity || '1',
        headingColor: heroSection?.heading_color || defaultGrayColor,
        headingWeight: heroSection?.heading_weight || '400',
        headingSize: heroSection?.heading_size || '50',
        headingFontFamily: heroSection?.heading_font_family || 'inherit',
        subheadingColor: heroSection?.subheading_color || defaultGrayColor,
        subheadingWeight: heroSection?.subheading_weight || '400',
        subheadingSize: heroSection?.subheading_size || '22',
        subheadingFontFamily: heroSection?.subheading_font_family || 'inherit',
        buttonColor: heroSection?.button_color || defaultDarkGrayColor,
        buttonTextColor: heroSection?.button_text_color || defaultWhiteColor,
        buttonTextWeight: heroSection?.button_text_weight || '600',
        buttonTextCasing: heroSection?.button_text_casing || 'uppercase',
        headingTextCasing: heroSection?.heading_text_casing || 'uppercase',
        subheadingTextCasing: heroSection?.subheading_text_casing || 'uppercase',
        buttonFontFamily: heroSection?.button_font_family || 'inherit',
        contentAlignment: heroSection?.content_alignment || 'center',
        topSpacing: heroSection?.top_spacing || '80',
        bottomSpacing: heroSection?.bottom_spacing || '80',
      },
      talentCommunityStyles: {
        ...styleControlsFormatted?.talentCommunitySection,
      },
      talentCommunitySection,
    }));
  }, [data]);

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const updateField = (fieldName) => (value) => {
    let errors = { ...formErrors, [fieldName]: false };
    let cleanButtonText = {};

    if (fieldName === 'openRolesButton' && !value) {
      cleanButtonText = {
        buttonText: '',
      };
      errors = {
        ...errors,
        buttonText: false,
      };
    }

    setFormErrors(errors);
    setIsSaveBarActive(true);
    setForm({
      ...form,
      [fieldName]: value,
      ...cleanButtonText,
    });
  };

  const updateFontValue =
    ({ sectionName, fieldName }) =>
    (value) => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        [sectionName]: {
          ...form[sectionName],
          [fieldName]: `${value}`,
        },
      });
    };

  const updateColor =
    ({ sectionName, fieldName }) =>
    (color) => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        [sectionName]: {
          ...form[sectionName],
          [fieldName]: color,
        },
      });
    };

  const updateImage = (file) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      heroBackgroundImage: file,
    });
    setHeroBackgroundChanged(true);
  };

  const updateOpacity = (value) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      heroSection: {
        ...form.heroSection,
        backgroundOpacity: `${(value / 100).toFixed(2)}`,
      },
    });
  };

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    refetch();
    setTimeout(() => {
      setIsSaveBarActive(false);
      setShowSuccessBanner(true);
      setIsLoading(false);
    }, 1000);
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const onSubmit =
    (publish = true) =>
    (e) => {
      e?.preventDefault();
      setPreview(false);
      setConfirmModal(false);
      const openRolesButton = compose((value) => value, prop('openRolesButton'))(form);
      const updatedErrors = {
        headingText: compose(isEmpty, prop('headingText'))(form),
        subheadingText: compose(isEmpty, prop('subheadingText'))(form),
        buttonText: openRolesButton ? compose(isEmpty, prop('buttonText'))(form) : false,
      };

      setFormErrors(updatedErrors);

      if (
        compose(
          find((item) => item),
          values
        )(updatedErrors)
      ) {
        setTimeout(() => {
          onSubmitFailed();
        }, 10);
      } else {
        setIsLoading(true);
        const careerPortal = propOr({}, 'careerPortal')(data);
        const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
        const variables = {
          ...careerPortal,
          ...form,
          shopifyToken,
          sectionHero: publish,
        };
        delete variables.styleControls;

        if (!heroBackgroundChanged) {
          delete variables.heroBackgroundImage;
        }

        save({ variables }).then(onSuccess).catch(onError);
      }
    };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  const isSubscribedUser = !isEmpty(currentSubscription);

  return (
    <AppProvider
      i18n={{
        Polaris: {
          DropZone: {
            actionTitle: 'Add image',
          },
        },
      }}>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'Career Portal', onAction: () => { changeRoute('/career-portal-index'); } }]}
          title='Hero section'
          subtitle={filterPageTimeLabel('section_hero')}
          titleMetadata={
            <Badge status={form.sectionHero ? 'success' : 'default'}>{form.sectionHero ? 'Published' : 'Unpublished'}</Badge>
          }
          pagination={{
            hasPrevious: false,
            hasNext: !isSaveBarActive,
            nextURL: !isSaveBarActive ? `/career-portal/intro-paragraph-section` : null,
            previousURL: null,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <ButtonGroup>
              <Button monochrome plain onClick={togglePreviewModal}>
                Preview
              </Button>
              <Button
                primary
                disabled={!isSaveBarActive && form.sectionHero}
                onClick={onSubmit(true)}
                connectedDisclosure={
                  form.sectionHero
                    ? {
                        accessibilityLabel: 'Change section status',
                        actions: [{ content: 'Unpublish', onAction: toggleConfirmModal }],
                      }
                    : undefined
                }>
                Publish
              </Button>
            </ButtonGroup>
          }>
          <Form onSubmit={onSubmit()}>
            {showSuccessBanner && (
              <SuccessBanner
                history={history}
                title='Hero section updated'
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={'View on your career portal'}
                detailsBody={'go to all career portal sections'}
                portalUrl={careerPageLink}
                detailsUrl={'/career-portal-index'}
              />
            )}
            <Layout>
              <Layout.AnnotatedSection
                title='Content'
                description='The hero section represents the top portion of your career portal pages. Make it bold, engaging and representative of your brand.'>
                <Card sectioned>
                  <FormLayout>
                    <div>
                      <TextField
                        id='headingText'
                        label='Title text'
                        value={form.headingText}
                        name='headingText'
                        onChange={updateField('headingText')}
                        placeholder='View Our Open Roles'
                      />
                      {formErrors.headingText && <InlineError message='Title text is required' fieldID='headingText' />}
                    </div>
                    <div>
                      <TextField
                        id='subheadingText'
                        label='Subtitle text'
                        value={form.subheadingText}
                        name='subheadingText'
                        onChange={updateField('subheadingText')}
                        placeholder='We’re looking for great people to join our growing team'
                      />
                      {formErrors.subheadingText && <InlineError message='Subtitle text is required' fieldID='subheadingText' />}
                    </div>
                    <div>
                      <TextField
                        id='buttonText'
                        label='Button text'
                        value={form.buttonText}
                        name='buttonText'
                        onChange={updateField('buttonText')}
                      />
                      {formErrors.buttonText && <InlineError message='Button text is required' fieldID='buttonText' />}
                    </div>
                    <TextStyle variation='subdued'>Visitors can click this button to jump to the job listings section</TextStyle>
                    <Checkbox
                      name='openRolesButton'
                      id='openRolesButton'
                      label='Display button'
                      ariaControls='basic-collapsible'
                      checked={form.openRolesButton}
                      onChange={updateField('openRolesButton')}
                    />
                  </FormLayout>
                  <br />
                  <FormLayout>
                    <FormLayout.Group>
                      <div className={'subsection_header'}>
                        <Subheading>Upload background image</Subheading>
                      </div>
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <div>
                        <DropZone allowMultiple={false} onDrop={handleDropZoneDrop}>
                          {uploadedFile}
                          {fileUpload}
                        </DropZone>
                        <div style={{ marginTop: '10px' }}>
                          <TextStyle variation='subdued'>JPEG or PNG, 1600 X 500 pixels</TextStyle>
                        </div>
                      </div>
                      <div>
                        <Card>
                          <Card.Section>
                            <img style={{ width: '80%' }} src={renderPreviewPhoto()} />
                          </Card.Section>
                        </Card>
                      </div>
                    </FormLayout.Group>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                      <div style={{ width: '60%', marginTop: '15px' }}>
                        <RangeSlider
                          label='Opacity'
                          value={opacityValue}
                          onChange={handleOpacityChange}
                          output
                          max={100}
                          disabled={!isSubscribedUser}
                          min={0}
                          suffix={`${opacityValue}%`}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                        <Button primary disabled={!form?.heroBackgroundImage} onClick={handleRemoveBackgroundImage}>
                          Remove
                        </Button>
                      </div>
                    </div>
                  </FormLayout>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Layout'
                description='Customize the content alignment and spacing to match your store’s theme.'>
                <Card sectioned>
                  <FormLayout>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Content alignment'
                        options={CONTENT_ALIGNMENT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'contentAlignment' })}
                        value={form.heroSection?.contentAlignment}
                      />
                    </div>
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Top spacing'
                        value={form.heroSection?.topSpacing}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'topSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.heroSection?.topSpacing}px`}
                      />
                      <RangeSlider
                        label='Bottom spacing'
                        value={form.heroSection?.bottomSpacing}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'bottomSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.heroSection?.bottomSpacing}px`}
                      />
                    </div>
                  </FormLayout>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Appearance'
                description='Customize the font and section background colors to match your store’s theme.'>
                <Card sectioned>
                  <Card.Section title='SECTION'>
                    <ColorPicker
                      colorName='Background color'
                      defaultColor={form.heroSection?.backgroundColor}
                      onSelectColor={updateColor({ sectionName: 'heroSection', fieldName: 'backgroundColor' })}
                    />
                  </Card.Section>
                  <Card.Section title='TITLE TEXT'>
                    <ColorPicker
                      colorName='Title text color'
                      defaultColor={form.heroSection?.headingColor}
                      onSelectColor={updateColor({ sectionName: 'heroSection', fieldName: 'headingColor' })}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Title font size'
                        value={form.heroSection?.headingSize}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'headingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.heroSection?.headingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'headingFontFamily' })}
                        value={form.heroSection?.headingFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'headingWeight' })}
                        value={form.heroSection?.headingWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'headingTextCasing' })}
                        value={form.heroSection?.headingTextCasing}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='SUBTITLE TEXT'>
                    <ColorPicker
                      colorName='Subtitle text color'
                      defaultColor={form.heroSection?.subheadingColor}
                      onSelectColor={updateColor({ sectionName: 'heroSection', fieldName: 'subheadingColor' })}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Subtitle font size'
                        value={form.heroSection?.subheadingSize}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'subheadingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.heroSection?.subheadingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Subtitle font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'subheadingFontFamily' })}
                        value={form.heroSection?.subheadingFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Subtitle font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'subheadingWeight' })}
                        value={form.heroSection?.subheadingWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Subtitle text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'subheadingTextCasing' })}
                        value={form.heroSection?.subheadingTextCasing}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='BUTTON'>
                    <ColorPicker
                      colorName='Button color'
                      defaultColor={form.heroSection?.buttonColor}
                      onSelectColor={updateColor({ sectionName: 'heroSection', fieldName: 'buttonColor' })}
                    />
                    <ColorPicker
                      colorName='Button text color'
                      defaultColor={form.heroSection?.buttonTextColor}
                      onSelectColor={updateColor({ sectionName: 'heroSection', fieldName: 'buttonTextColor' })}
                    />
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'buttonFontFamily' })}
                        value={form.heroSection?.buttonFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button text font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'buttonTextWeight' })}
                        value={form.heroSection?.buttonTextWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'heroSection', fieldName: 'buttonTextCasing' })}
                        value={form.heroSection?.buttonTextCasing}
                      />
                    </div>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='SEO'
                description='The hero section represents the top portion of your career portal pages. Make it bold, engaging and representative of your brand.'>
                <Card sectioned>
                  <FormLayout>
                    <TextField
                      id='pageTitle'
                      label='Page title'
                      value={form.pageTitle}
                      name='pageTitle'
                      onChange={updateField('pageTitle')}
                      placeholder='Careers | [store name]'
                    />
                    <TextField
                      id='metaDescription'
                      label='Meta description'
                      value={form.metaDescription}
                      name='metaDescription'
                      multiline={5}
                      onChange={updateField('metaDescription')}
                    />
                  </FormLayout>
                </Card>
              </Layout.AnnotatedSection>
            </Layout>
            <div className='career-portal__form-buttons'>
              <Button onClick={() => changeRoute('/career-portal-index')}>Cancel</Button>
              <Button primary submit disabled={!isSaveBarActive && form.sectionHero}>
                Publish
              </Button>
            </div>
          </Form>
          <Modal
            open={!!preview}
            large
            onClose={togglePreviewModal}
            primaryAction={{
              content: 'Publish',
              disabled: !isSaveBarActive && form.sectionHero,
              onAction: onSubmit(),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: togglePreviewModal,
              },
            ]}
            title={
              <TextContainer>
                <Heading>Preview hero section</Heading>
                <p style={{ fontSize: '0.85em' }}>
                  Your theme fonts are not applied in the preview bellow, publish the section to your career portal to see how the
                  section will look on your store
                </p>
              </TextContainer>
            }
            loading={false}>
            <div className={'preview__modal'}>
              <section
                className={'hero_section'}
                style={{
                  paddingTop: `${form.heroSection?.topSpacing || '80'}px`,
                  paddingBottom: `${form.heroSection?.bottomSpacing || '80'}px`,
                  backgroundColor: `${hslaColor(form.heroSection?.backgroundColor, '#197bbd')}`,
                }}>
                <div
                  className='opacity-overlay'
                  style={{
                    opacity: `${form.heroSection?.backgroundOpacity || 1}`,
                    backgroundColor: `${hslaColor(form.heroSection?.backgroundColor, '#197bbd')}`,
                    backgroundImage: `url(${renderPreviewPhoto(false)})`,
                  }}></div>
                <div
                  className='wrapper'
                  style={{ alignItems: `${form.heroSection?.contentAlignment === 'left' ? 'flex-start' : 'center'}` }}>
                  <h1
                    className='heading_text'
                    style={{
                      color: `${hslaColor(form.heroSection?.headingColor, '#fff')}`,
                      textAlign: `${form.heroSection?.contentAlignment || 'center'}`,
                      fontSize: `${form.heroSection?.headingSize || '22'}px`,
                      fontWeight: `${form.heroSection?.headingWeight || '700'}`,
                    }}>
                    {form.headingText}
                  </h1>
                  <h2
                    className='subheading_text'
                    style={{
                      color: `${hslaColor(form.heroSection?.subheadingColor, '#fff')}`,
                      textAlign: `${form.heroSection?.contentAlignment || 'center'}`,
                      fontSize: `${form.heroSection?.subheadingSize || '22'}px`,
                      fontWeight: `${form.heroSection?.subheadingWeight || '700'}`,
                    }}>
                    {form.subheadingText}
                  </h2>
                  <a
                    className='hero_button'
                    style={{
                      backgroundColor: `${hslaColor(form.heroSection?.buttonColor, '#3a3a3a')}`,
                      color: `${hslaColor(form.heroSection?.buttonTextColor, '#fff')}`,
                    }}>
                    {form.buttonText || 'View open roles'}
                  </a>
                </div>
              </section>
            </div>
          </Modal>
          <Modal
            open={confirmModal}
            onClose={toggleConfirmModal}
            title={`${form.sectionHero ? 'Unpublish' : 'Publish'} section`}
            primaryAction={{
              content: `Yes, ${form.sectionHero ? 'unpublish' : 'publish'} section`,
              onAction: onSubmit(!form.sectionHero),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: toggleConfirmModal,
              },
            ]}
            loading={false}>
            <Modal.Section>
              <p>{`Do you want to ${form.sectionHero ? 'unpublish' : 'publish'} this section to your career portal page?`}</p>
            </Modal.Section>
          </Modal>
        </Page>
        {toastMarkup}
      </Frame>
    </AppProvider>
  );
};

export default HeroSection;
